import { ReactElement, ReactNode, RefObject, forwardRef, memo, useEffect, useRef } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { Box, Grid, SxProps, Theme } from '@mui/material';
import { mergeSxProps } from 'common/theme';

import { useDragAndDrop, useSortableItem } from './hooks';

type SortableItemChildrenRenderProp = (props: {
  handlerRef?: RefObject<HTMLDivElement>;
  isDragging: boolean;
}) => ReactElement;

interface SortableItemProps {
  droppableType: string;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  canDrag?: boolean;
  className?: string;
  contentClassName?: string;
  sx?: SxProps<Theme> | undefined;
  isGridItem?: boolean;
  itemData?: object;
  collectItemData?: (index: number) => object;
  customPreview?: boolean;
  onDrop?(endIndex: number): void;
  onDragStart?(): void;
  disabled?: boolean;
  children: ReactNode | SortableItemChildrenRenderProp;
}

export const SortableItem = memo(
  forwardRef<unknown, SortableItemProps>(
    (
      {
        droppableType,
        index,
        moveItem,
        canDrag = true,
        className,
        contentClassName,
        sx,
        children,
        isGridItem = false,
        itemData,
        collectItemData,
        customPreview,
        onDrop,
        onDragStart,
        disabled,
      },
      forwardedRef
    ) => {
      const ref = useRef<HTMLDivElement>(null);
      const startIndexRef = useRef<number>();
      const handlerRef = useRef<HTMLDivElement>(null);

      const { isDragging, handlerId, preview } = useDragAndDrop({
        index,
        canDrag,
        itemData: collectItemData ? collectItemData(index) : itemData,
        previewRef: ref,
        handlerRef,
        droppableType,
        customPreview,
        moveItem,
      });

      useSortableItem({ onDrop, onDragStart, isDragging, startIndexRef, index });

      useEffect(() => {
        if (customPreview) {
          preview(getEmptyImage(), { captureDraggingState: true });
        }
      }, [customPreview, preview]);

      const renderChildren = () =>
        typeof children === 'function'
          ? children({ handlerRef: disabled ? undefined : handlerRef, isDragging })
          : children;

      return (
        <Box
          component={isGridItem ? Grid : Box}
          item={isGridItem || undefined}
          className={className}
          ref={disabled ? undefined : ref}
          data-handler-id={handlerId}
          data-testid={`SortableItem-${index}`}
          sx={mergeSxProps(sx, isDragging ? { cursor: 'grabbing', opacity: 0.5 } : {})}
        >
          <Box ref={forwardedRef} className={contentClassName}>
            {renderChildren()}
          </Box>
        </Box>
      );
    }
  )
);
